import { validations } from "../Forms/Default";
import { mergeDeep } from "../../../../utils/Helper";
import { Mode } from "../../../../utils";
import { IOMode, IOType } from "../../../IO/types";

export const inOutReducer = (state, action) => {
  switch (action.type) {
    case "CHANGE_MODE":
      switch (action.mode) {
        case Mode.EDIT:
          return processEdit(state);
        case Mode.PRECHECK:
          return processPrecheck(state);
        case Mode.CONFIRM:
          return processConfirm(state);
        default:
          return state;
      }
    case "UPDATE_ACCOUNT_BANKS":
      return processUpdateAccountBanks(state, action.accountBanks);
    case "UPDATE_FILES":
      return processUpdateFiles(state, action);
    case "UPDATE_PRECHECK":
      return updatePrecheck(state, action.fields);
    case "EDIT_BULK_ITEM":
      return processEditBulkItem(state, action.item);
    case "DELETE_BULK_ITEM":
      return processDeleteBulkItem(state, action.itemId);
    case "CHANGE_IO_FIELDS":
      return processChangeIOFields(state, action.fields);
    case "CHANGE_IO_ITEMS_HEADER":
      return processChangeIOItemsHeader(state, action.payload.fields);
    case "CHANGE_IO_ITEM_FIELDS":
      return processChangeIOItem(state, action.payload.itemId || 0, action.payload.fields);
    case "CHANGE_BUTTON_PROPS":
      return changeButtonProps(state, action);
    default:
      return state;
  }
};

function processUpdateAccountBanks(state, accountBanks) {
  return mergeDeep(
    { ...state },
    {
      entities: {
        accountBanks,
      },
    }
  );
}

function processUpdateFiles(state, action) {
  const { type, files, id } = action.props;
  if (type == "Add") {
    return mergeDeep(
      { ...state },
      {
        io: {
          files: [...state.io.files, ...files],
        },
      }
    );
  } else if (type == "Delete"){
    return mergeDeep(
      { ...state },
      {
        io: {
          files: state.io.files.filter((f) => f.id != id),
          removedItem: true,
        },
      }
    );
  } else {
    return mergeDeep(
      { ...state },
      {
        io: {
          files: state.io.files.filter((f) => f.id != id),
        },
      }
    );
  }
}

function processChangeIOFields(state, fields) {
  const errors = Object.keys(fields).reduce((f, name) => {
    f[name] = undefined;
    return f;
  }, {});

  return mergeDeep(
    { ...state },
    {
      io: {
        ...fields,
      },
      fields: {
        errors,
      },
    }
  );
}

const updatePrecheck = (state, props) => {
  const newProps = {
    precheck: {
      ...props,
    },
  };

  return mergeDeep({ ...state }, { ...newProps });
};

function processChangeIOItemsHeader(state, fields) {
  const errors = Object.keys(fields).reduce((f, name) => {
    f[`header_${name}`] = undefined;
    return f;
  }, {});

  return mergeDeep(
    { ...state },
    {
      io: {
        itemsHeader: {
          ...fields,
        },
      },
      fields: {
        errors,
      },
    }
  );
}

function processChangeIOItem(state, itemId, fields) {
  const { Id, IOMode: ioMode } = state.io;
  const errors = {};

  let changedItems = state.ui.changedItems || [];

  // require update errors
  if (ioMode == IOMode.Simple) {
    Object.keys(fields).forEach((f) => (errors[`simple_${f}`] = undefined));
  }

  const items = state.io.items.map((i) => {
    if (i.Id == itemId) {
      return {
        ...i,
        ...fields,
      };
    }
    return i;
  });

  if (ioMode == IOMode.Simple && Id != undefined) {
    changedItems = ["1"];
  }

  return mergeDeep(
    { ...state },
    {
      ui: {
        changedItems,
      },
      io: {
        items,
      },
      fields: {
        errors,
      },
    }
  );
}

function processDeleteBulkItem(state, itemId) {
  let deletedItems = state.ui.deletedItems || [];
  const item = state.io.items.find((i) => i.Id == itemId);

  if (!item.isNew) {
    deletedItems.push(item);
  }

  return mergeDeep(
    { ...state },
    {
      ui: {
        deletedItems,
      },
      io: {
        items: state.io.items.filter((i) => i.Id != itemId),
      },
    }
  );
}

function processEditBulkItem(state, item) {
  const { io } = state;
  let changedItems = state.ui.changedItems || [];
  let addedItems = state.ui.addedItems || [];

  const updatedItems = io.items.map((i) => {
    if (i.Id == item.Id) return item;

    return i;
  });

  if (item.Id == undefined) {
    item.isNew = true;
    item.Id = Math.random().toString(36).substring(9);
    item.Correlative = item.Id;
    addedItems.push(item.Id);
    updatedItems.push(item);
  } else if (!item.isNew) {
    changedItems.push(item.Id);
  }

  return mergeDeep(
    { ...state },
    {
      ui: {
        changedItems,
        addedItems,
      },
      io: {
        items: updatedItems,
      },
      fields: {
        errors: {
          items: undefined,
        },
      },
    }
  );
}

function processEdit(state) {
  // remove readonly
  const readonly = {};

  Object.keys(state.fields.readonly).forEach((f) => (readonly[f] = false));

  return mergeDeep(
    { ...state },
    {
      ui: {
        mode: Mode.EDIT,
      },
      buttons: {
        precheck: {
          visible: true,
        },
        modify: {
          visible: false,
        },
        confirm: {
          visible: false,
          loading: false,
        },
      },
      fields: {
        readonly,
      },
      precheck: {
        authorizations: [],
      },
    }
  );
}

function processPrecheck(state) {
  const errors = validateForm(state);

  if (errors == false) {
    const readonly = {};
    const { itemsHeader, ...others } = state.io;

    Object.keys(others).forEach((f) => {
      readonly[f] = true;
    });

    Object.keys(itemsHeader).forEach((f) => {
      readonly[`header_${f}`] = true;
    });

    return mergeDeep(
      { ...state },
      {
        ui: {
          mode: Mode.PRECHECK,
        },
        buttons: {
          precheck: {
            loading: true,
          },
        },
        fields: {
          readonly,
        },
      }
    );
  } else {
    return mergeDeep(
      { ...state },
      {
        fields: {
          errors,
        },
      }
    );
  }
}

function processConfirm(state) {
  const { precheck } = state;

  return mergeDeep(
    { ...state },
    {
      buttons: {
        precheck: {
          visible: false,
          loading: false,
        },
        modify: {
          visible: true,
        },
        confirm: {
          visible: precheck.status != "failed",
          loading: false,
        },
      },
    }
  );
}

function validateForm(state) {
  const { io, fields, userType } = state;
  const { subForm, simple, ...form } = validations(io, userType);
  const { readonly } = fields;

  const errors = {};

  Object.entries(form).forEach((entry) => {
    const [name, value] = entry;
    if (!readonly[name]) {
      if (value.empty) {
        if (io[name] == undefined || io[name] === "") {
          errors[name] = "Required field";
        } else if (value.autosuggestId && io[value.autosuggestId] == undefined) {
          errors[name] = "Must select a value from list";
        }
      }
    }
  });

  Object.entries(simple).forEach((entry) => {
    const [name, value] = entry;
    if (!readonly[`simple_${name}`]) {
      if (value.empty) {
        if (io.items[0][name] == undefined || io.items[0][name] === "") {
          errors[`simple_${name}`] = "Required field";
        } else if (value.autosuggestId && io.items[0][value.autosuggestId] == undefined) {
          errors[`simple_${name}`] = "Must select a value from list";
        }
      }
      if (name == "Denominations") {
        if (value.DenominationsSlots == false) {
          errors["simple_Denominations"] =
            "Must select " + value.slots + " denominations for the quantity selected";
        }
      }
    }
  });

  Object.entries(subForm).forEach((entry) => {
    const [name, value] = entry;
    if (!readonly[name]) {
      if (value.empty) {
        if (io.itemsHeader[name] == undefined || io.itemsHeader[name] === "") {
          errors[`header_${name}`] = "Required field";
        } else if (value.autosuggestId && io.itemsHeader[value.autosuggestId] == undefined) {
          errors[`header_${name}`] = "Must select a value from list";
        }
      }
    }
  });

  if (io.IOMode == IOMode.MultipleAssets || io.IOType == IOType.Transfer) {
    if (io.items.length == 0) {
      errors.items = "Require at least 1 item";
    }

    if ((io.IOType == IOType.Withdraw || io.IOType == IOType.Transfer) && !errors.items) {
      const missing = io.items.filter(
        (item) =>
          item.RequiresDenominations &&
          !item.IsCurrency &&
          (!Array.isArray(item.Denominations) || item.Denominations.length == 0)
      );

      if (missing.length > 0) {
        const tickers = missing.map((item) => item.AssetTicker).filter(Boolean);
        errors.items =
          "Select denominations for: " +
          (tickers.length > 0 ? tickers.join(", ") : "the selected assets");
      }
    }

    delete errors.CurrencyISO;
    delete errors.AssetTicker;
    delete errors.Quantity;
  }

  // has Errors only add errors to state
  if (Object.keys(errors).length !== 0) {
    return errors;
  }

  return false;
}

function changeButtonProps(state, { props }) {
  return mergeDeep({ ...state }, { buttons: { ...props } });
}

export function getTitle({ IOType, mode }, uiMode) {
  let title = `New `;

  if (uiMode == Mode.READONLY) {
    title = `View `;
  }

  if (mode == IOMode.MultipleAssets) {
    title += `Bulk `;
  }

  return `${title} ${IOType}`;
}
